import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowRight, Zap, Target, BarChart3, Code, BookOpen, Play, Microscope, Brain, Shield } from 'lucide-react';

const PageContainer = styled.div`
  padding-top: 64px;
`;

// Hero uses a two-column layout (text + diagnostic summary panel)
const HeroSection = styled.section`
  background: radial-gradient(circle at top left, ${props => props.theme.colors.surface} 0%, ${props => props.theme.colors.background} 60%);
  padding: ${props => props.theme.spacing[20]} 0 ${props => props.theme.spacing[16]};
  min-height: 80vh;
  display: flex;
  align-items: center;
`;

const HeroGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[6]};
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: ${props => props.theme.spacing[10]};

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const HeroContent = styled.div`
  padding-right: ${props => props.theme.spacing[4]};
`;

const HeroSidePanel = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[6]};
  box-shadow: ${props => props.theme.shadows.lg};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const HeroTitle = styled(motion.h1)`
  font-size: ${props => props.theme.fontSizes['5xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  margin-bottom: ${props => props.theme.spacing[6]};
  line-height: 1.1;
  color: ${props => props.theme.colors.text.primary};
`;

const HeroSubtitle = styled(motion.p)`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.text.secondary};
  margin-bottom: ${props => props.theme.spacing[6]};
  line-height: 1.7;
  max-width: 40rem;
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: ${props => props.theme.spacing[4]};
  justify-content: flex-start;
  flex-wrap: wrap;
`;

const Button = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[4]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-weight: ${props => props.theme.fontWeights.medium};
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: ${props => props.theme.fontSizes.lg};
`;

const PrimaryButton = styled(Button)`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};

  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const SecondaryButton = styled(Button)`
  background: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.text.primary};
  border: 2px solid ${props => props.theme.colors.border};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${props => props.theme.spacing[4]};
`;

const MetricCard = styled.div`
  padding: ${props => props.theme.spacing[4]};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.background};
`;

const MetricLabel = styled.div`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: ${props => props.theme.spacing[1]};
`;

const MetricValue = styled.div`
  font-size: ${props => props.theme.fontSizes['2xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.primary};
`;

const MetricDescription = styled.div`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin-top: ${props => props.theme.spacing[1]};
`;

const FeaturesSection = styled.section`
  padding: ${props => props.theme.spacing[16]} 0;
  background: ${props => props.theme.colors.surface};
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing[12]};
  font-size: ${props => props.theme.fontSizes['4xl']};
  color: ${props => props.theme.colors.text.primary};
`;

const FeaturesGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[4]};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing[8]};
`;

const FeatureCard = styled(motion.div)`
  background: ${props => props.theme.colors.background};
  padding: ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const FeatureIcon = styled.div`
  width: 60px;
  height: 60px;
  background: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing[4]};
  color: ${props => props.theme.colors.text.inverse};
`;

const FeatureTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  margin-bottom: ${props => props.theme.spacing[3]};
  color: ${props => props.theme.colors.text.primary};
`;

const FeatureDescription = styled.p`
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

const CTASection = styled.section`
  padding: ${props => props.theme.spacing[16]} 0;
  background: ${props => props.theme.colors.background};
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing[4]};
`;

const LandingPage = () => {
  const features = [
    {
      icon: <Brain size={24} />,
      title: 'Deep Learning Models',
      description: 'State-of-the-art CNN architectures including VGG16, ResNet-50, and EfficientNet with transfer learning for optimal performance.'
    },
    {
      icon: <Target size={24} />,
      title: 'High Accuracy',
      description: 'EfficientNet-B0 achieves 97.32% accuracy on the NIH cell images dataset with strong precision, F1-score, and ROC-AUC.'
    },
    {
      icon: <BarChart3 size={24} />,
      title: 'Explainable AI',
      description: 'Grad-CAM visualization provides interpretable insights into model predictions, highlighting regions of interest in images.'
    },
    {
      icon: <Code size={24} />,
      title: 'Production Ready',
      description: 'FastAPI backend with React frontend, Docker containerization, and comprehensive API documentation for easy deployment.'
    },
    {
      icon: <BookOpen size={24} />,
      title: 'Comprehensive Docs',
      description: 'Detailed documentation, API reference, examples, and deployment guides for seamless integration and customization.'
    },
    {
      icon: <Microscope size={24} />,
      title: 'Clinical Support',
      description: 'Designed as a clinical decision support tool with confidence scores and visual explanations for healthcare professionals.'
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroGrid>
          <HeroContent>
            <HeroTitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Malaria Detection for Clinical Workflows
            </HeroTitle>
            <HeroSubtitle
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Upload peripheral blood smear images and obtain fast, reliable screening for malaria infection.
              The system combines transfer learning and explainable AI to support, not replace, expert diagnosis.
            </HeroSubtitle>
            <HeroButtons
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <PrimaryButton to="/demo">
                Open Interactive Demo
                <ArrowRight size={20} />
              </PrimaryButton>
              <SecondaryButton to="/docs">
                View Technical Details
                <BookOpen size={20} />
              </SecondaryButton>
            </HeroButtons>
          </HeroContent>

          <HeroSidePanel>
            <div>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.5rem' }}>Model snapshot</p>
              <MetricsGrid>
                <MetricCard>
                  <MetricLabel>Best model accuracy</MetricLabel>
                  <MetricValue>97.32%</MetricValue>
                  <MetricDescription>EfficientNet-B0 (Parasitized vs Uninfected)</MetricDescription>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Images processed</MetricLabel>
                  <MetricValue>27,560</MetricValue>
                  <MetricDescription>NIH cell images dataset (13,780 per class)</MetricDescription>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>ROC-AUC</MetricLabel>
                  <MetricValue>0.9948</MetricValue>
                  <MetricDescription>Strong class separability on held-out test split</MetricDescription>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Explainability</MetricLabel>
                  <MetricValue>Grad-CAM</MetricValue>
                  <MetricDescription>Heatmaps highlight parasite-relevant regions</MetricDescription>
                </MetricCard>
              </MetricsGrid>
            </div>
            <div>
              <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '0.75rem' }}>Designed for</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '0.75rem' }}>
                <MetricCard>
                  <MetricLabel>Clinics</MetricLabel>
                  <MetricDescription>Rapid triage in low-resource settings</MetricDescription>
                </MetricCard>
                <MetricCard>
                  <MetricLabel>Researchers</MetricLabel>
                  <MetricDescription>Benchmarking and model experimentation</MetricDescription>
                </MetricCard>
              </div>
            </div>
          </HeroSidePanel>
        </HeroGrid>
      </HeroSection>

      <FeaturesSection>
        <SectionTitle>Key Features</SectionTitle>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <FeatureIcon>{feature.icon}</FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </FeaturesSection>

      <CTASection>
        <CTAContent>
          <h2>Ready to Get Started?</h2>
          <p style={{ marginBottom: '2rem', fontSize: '1.125rem', color: '#64748b' }}>
            Explore our documentation, try the live demo, or integrate our API into your applications.
          </p>
          <HeroButtons>
            <PrimaryButton to="/demo">
              Start with Demo
              <Play size={20} />
            </PrimaryButton>
            <SecondaryButton to="/docs">
              Read Documentation
              <BookOpen size={20} />
            </SecondaryButton>
          </HeroButtons>
        </CTAContent>
      </CTASection>
    </PageContainer>
  );
};

export default LandingPage;

